import { StandardListFilterSchema } from '@input-web/modules/standard/models/standard-list-filter.model';
import { GetSubjectUniversityOptionListBody } from '@master/models/get-subject-university-option-list.model';
import { NavigateOptions } from '@tanstack/react-router';
import { useLayoutEffect, useMemo, useRef } from 'react';

// #region Types
type UseStandardLayoutFilterProps = {
  onSearchStateChange?: (newSearch: StandardListFilterSchema, options?: NavigateOptions) => void;
  optionList?: GetSubjectUniversityOptionListBody;
  searchState?: StandardListFilterSchema;
};
// #endregion

export default function useStandardLayoutFilter({
  onSearchStateChange,
  optionList,
  searchState,
}: UseStandardLayoutFilterProps) {
  // #region Computed Values
  const subjectOptions = useMemo(
    () =>
      optionList?.subjectNames.map((subject) => ({
        label: subject.subjectName,
        value: subject.subjectId,
      })) ?? [],
    [optionList],
  );

  const universityOptions = useMemo(
    () =>
      optionList?.universities
        .filter((university) => !!university?.subCategories?.length)
        .map((university) => ({
          label: university.universityName,
          value: university.universityId,
        })) ?? [],
    [optionList],
  );

  const bunriOptions = useMemo(
    () =>
      optionList?.universities
        .find(({ universityId }) => universityId === searchState?.universityId)
        ?.subCategories?.map(
          (subCategory) =>
            'bunriId' in subCategory && {
              label: subCategory.bunriName,
              value: subCategory.bunriId,
            },
        )
        .filter((item): item is Exclude<typeof item, false> => !!item) ?? [],
    [optionList, searchState?.universityId],
  );

  const facultyOptions = useMemo(
    () =>
      optionList?.universities
        .find(({ universityId }) => universityId === searchState?.universityId)
        ?.subCategories?.map(
          (subCategory) =>
            'facultyId' in subCategory && {
              label: subCategory.facultyName,
              value: subCategory.facultyId,
            },
        )
        .filter((item): item is Exclude<typeof item, false> => !!item) ?? [],
    [optionList, searchState?.universityId],
  );
  // #endregion

  // #region Effects
  const handleChangeRef = useRef(onSearchStateChange);
  handleChangeRef.current = onSearchStateChange;
  const changedSearchStateRef = useRef(searchState);
  useLayoutEffect(() => {
    if (!searchState || !optionList) return;

    const newSearch = searchState;
    let shouldChange = false;

    if (
      searchState?.subjectId != null &&
      !subjectOptions.find((item) => item.value === searchState?.subjectId)
    ) {
      shouldChange = true;
      newSearch.subjectId = undefined;
    }

    if (
      searchState?.universityId != null &&
      !universityOptions.find((item) => item.value === searchState?.universityId)
    ) {
      shouldChange = true;
      newSearch.universityId = undefined;
    }

    if (
      searchState?.bunriId != null &&
      !bunriOptions.find((item) => item.value === searchState?.bunriId)
    ) {
      shouldChange = true;
      newSearch.bunriId = undefined;
    }

    if (
      searchState?.facultyId != null &&
      !facultyOptions.find((item) => item.value === searchState?.facultyId)
    ) {
      shouldChange = true;
      newSearch.facultyId = undefined;
    }

    shouldChange &&=
      changedSearchStateRef.current?.bunriId !== newSearch.bunriId ||
      changedSearchStateRef.current?.facultyId !== newSearch.facultyId ||
      changedSearchStateRef.current?.universityId !== newSearch.universityId ||
      changedSearchStateRef.current?.subjectId !== newSearch.subjectId;
    if (!shouldChange) return;

    handleChangeRef.current?.(newSearch, { replace: true });
    changedSearchStateRef.current = newSearch;
  }, [bunriOptions, searchState, facultyOptions, subjectOptions, universityOptions, optionList]);
  // #endregion

  return { bunriOptions, facultyOptions, subjectOptions, universityOptions };
}
